import React from 'react';
import { AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { BakingSession, Starter } from '../../types/baking';
import { checkStarterHealth } from '../../lib/utils';
import { getCurrentActivity } from '../../components/starter/StarterUtils';
import { FERMENTATION_PROCESSES } from '../../types/baking';

interface StarterSelectorProps {
  starters: Starter[];
  selectedStarterId?: string;
  onSelect: (starterId: string) => void;
  existingSessions: BakingSession[];
  targetBakeTime?: Date;
}

function getActivityLevel(starter: Starter): { level: string; label: string } {
  if (!starter.lastFed) return { level: 'unknown', label: 'Unknown' };

  const hoursSinceLastFeed = (new Date().getTime() - new Date(starter.lastFed).getTime()) / (1000 * 60 * 60);
  const currentActivity = getCurrentActivity(
    hoursSinceLastFeed,
    starter.storage,
    starter.roomTemperature,
    starter.feedingRatio
  );

  if (currentActivity >= 80) {
    return { level: 'high', label: 'High Activity' };
  } else if (currentActivity >= 30) {
    return { level: 'medium', label: 'Mid. Activity' };
  } else {
    return { level: 'low', label: 'Low Activity' };
  }
}

export function StarterSelector({ 
  starters = [], 
  selectedStarterId, 
  onSelect,
  existingSessions,
  targetBakeTime 
}: StarterSelectorProps) {
  const getStarterAvailability = (starter: Starter) => {
    if (!targetBakeTime) return { available: true, reason: null };
    
    const timeDiff = targetBakeTime.getTime() - new Date().getTime();
    const hoursUntilBake = timeDiff / (1000 * 60 * 60);
    
    // Get current activity
    const hoursSinceLastFeed = (new Date().getTime() - new Date(starter.lastFed).getTime()) / (1000 * 60 * 60);
    const currentActivity = getCurrentActivity(
      hoursSinceLastFeed,
      starter.storage,
      starter.roomTemperature,
      starter.feedingRatio
    );
    
    if (hoursUntilBake < 18 && currentActivity < 50) {
      return {
        available: false,
        reason: 'Starter activity too low for short fermentation'
      };
    }
    
    return { available: true, reason: null };
  };

  const getExistingFeeds = (starterId: string) => {
    if (!targetBakeTime) return null;
    
    const timeDiff = targetBakeTime.getTime() - new Date().getTime();
    const hoursUntilBake = timeDiff / (1000 * 60 * 60);
    
    // If less than 12 hours until bake time, return no feeds needed
    if (hoursUntilBake < 12) {
      return {
        session: null,
        requiresOnlyLastFeed: false,
        noFeedsNeeded: true
      };
    }
    
    // If between 12 and 24 hours, return only last feed
    if (hoursUntilBake < 24) {
      return {
        session: null,
        requiresOnlyLastFeed: true,
        noFeedsNeeded: false
      };
    }

    const relevantSessions = existingSessions.filter(session => {
      if (session.starterId !== starterId) return false;
      const sessionTime = new Date(session.targetBakeTime);
      const timeDiff = Math.abs(sessionTime.getTime() - targetBakeTime.getTime());
      return timeDiff <= 24 * 60 * 60 * 1000; // Within 24 hours
    });

    if (relevantSessions.length === 0) return null;

    const sortedSessions = relevantSessions.sort((a, b) => 
      new Date(a.targetBakeTime).getTime() - new Date(b.targetBakeTime).getTime()
    );

    return {
      session: sortedSessions[0],
      requiresOnlyLastFeed: true,
      noFeedsNeeded: false
    };
  };

  if (starters.length === 0) {
    return (
      <div className="p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/10">
        <p className="text-sm text-white/80">
          You need to create a starter before you can start a new baking session.
          Visit the Starter page to create one.
        </p>
      </div>
    );
  }

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      {starters.map(starter => {
        const isSelected = selectedStarterId === starter.id;
        const health = checkStarterHealth(starter);
        const activity = getActivityLevel(starter);
        const hoursSinceLastFeed = (new Date().getTime() - new Date(starter.lastFed).getTime()) / (1000 * 60 * 60);
        const currentActivity = getCurrentActivity(
          hoursSinceLastFeed,
          starter.storage,
          starter.roomTemperature,
          starter.feedingRatio
        );
        
        const timeDiff = targetBakeTime ? targetBakeTime.getTime() - new Date().getTime() : 0;
        const hoursUntilBake = timeDiff / (1000 * 60 * 60);
        const isAvailable = !(hoursUntilBake < 18 && currentActivity < 50);
        const existingFeedInfo = getExistingFeeds(starter.id);
        
        return (
          <label
            key={starter.id}
            className={`block relative p-4 border rounded-xl transition-all duration-200 ${
              !isAvailable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${
              isSelected 
                ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10' 
                : 'border-white/10 bg-white/5 hover:bg-white/10'
            }`}
          >
            <input
              type="radio"
              name="starter"
              value={starter.id}
              checked={isSelected}
              onChange={() => isAvailable && onSelect(starter.id)}
              disabled={!isAvailable}
              className="sr-only"
            />

            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="font-medium text-white">{starter.name}</h3>
                <p className="text-sm text-white/70">
                  {starter.flourType} • {starter.texture} texture
                </p>
              </div>
              {isSelected && (
                <CheckCircle2 className="w-5 h-5 text-amber-300" />
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <p className="text-sm text-white/70">Last Fed</p>
                <p className="font-medium text-white">
                  {starter.lastFed ? formatDate(starter.lastFed) : 'Not set'}
                </p>
              </div>
              <div>
                <p className="text-sm text-white/70">Current Activity</p>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                  activity.level === 'high' 
                    ? 'bg-amber-500 text-white'
                    : activity.level === 'medium'
                    ? 'bg-amber-600/90 text-white/90'
                    : 'bg-white/20 text-white/80'
                }`}>
                  {activity.label}
                </span>
              </div>
            </div>


            {!isAvailable && (
              <div className="mt-3 flex items-start gap-2 p-3 bg-red-500/10 backdrop-blur-sm rounded-lg border border-red-500/20">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-300" />
                <p className="text-sm text-red-200">Starter activity too low for short fermentation</p>
              </div>
            )}

          </label>
        );
      })}
    </div>
  );
}